import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { FormFieldWrapper } from "../../layout";
import { EMPTY_SINGLE_VALUE } from "./select.constants";
import { getDefaultRenderValue, normalizeValue } from "./select.utils";

/**
 * @component BaseSelect
 *
 * Fully controlled select input.
 * No internal state. No form-library awareness.
 *
 * @param {Object} props
 * @param {string} props.id
 * @param {string | number | '' | Array<string | number>} props.value
 * @param {(value: string | number | Array<string | number>) => void} props.onChange
 * @param {{ label: string, value: string | number }[]} props.options
 * @param {string} [props.label]
 * @param {string} [props.placeholder='Select']
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.multiple=false]
 * @param {boolean} [props.error=false]
 * @param {string} [props.errorMessage]
 * @param {string} [props.helperText]
 * @param {boolean} [props.showErrorInline=true]
 * @param {boolean} [props.reserveErrorSpace=false]
 * @param {boolean} [props.fullWidth=false]
 * @param {'row' | 'column'} [props.orientation]
 * @param {'primary' | 'secondary'} [props.labelVariant]
 * @param {(value: any) => React.ReactNode} [props.renderValue]
 * @param {Object} [props.sx]
 * @param {Object} [props.wrapperSx]
 * @param {(option: Object) => React.ReactNode} [props.renderOption]
 * @returns {React.ReactElement}
 */
export default function BaseSelect(props: any) {
  const {
    id,
    value,
    onChange,
    options,

    label,
    placeholder = "Select",
    disabled = false,
    multiple = false,

    error = false,
    errorMessage,
    helperText,
    showErrorInline = true,
    reserveErrorSpace = false,

    fullWidth = false,
    orientation,
    renderValue,
    renderOption,

    sx = {},
    wrapperSx = {},
    labelVariant,
  } = props;

  const normalizedValue = normalizeValue(value, multiple);
  const shouldShowError = Boolean(showErrorInline && error);

  const resolvedRenderValue =
    renderValue ?? getDefaultRenderValue({ options, placeholder, multiple });

  return (
    <FormFieldWrapper
      label={label}
      htmlFor={id}
      orientation={orientation}
      error={shouldShowError}
      errorMessage={shouldShowError ? errorMessage : undefined}
      helperText={helperText}
      reserveErrorSpace={reserveErrorSpace}
      labelVariant={labelVariant}
      sx={wrapperSx}
    >
      <Select
        id={id}
        size="small"
        displayEmpty
        fullWidth={fullWidth}
        disabled={disabled}
        multiple={multiple}
        value={normalizedValue}
        renderValue={resolvedRenderValue}
        onChange={(e) => onChange(e.target.value)}
        error={shouldShowError}
        sx={{
          minWidth: 120,
          "&.Mui-disabled": {
            pointerEvents: "none",
            cursor: "default",
            opacity: 0.6,
          },
          ...sx,
        }}
      >
        {!multiple && (
          <MenuItem value={EMPTY_SINGLE_VALUE} disabled>
            {placeholder}
          </MenuItem>
        )}

        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {renderOption ? renderOption(opt) : opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormFieldWrapper>
  );
}
